import { OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TdLoadingService } from '@covalent/core';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { get, TruncateOptions } from 'lodash';

import { IPaging, IPagingSort } from './paging';
import { Category } from './category';
import { SuccessResponseObject } from './response-object';
import { Status, IdeaService } from '../services';
import { SearchParams } from '../components/idea-search-panel/idea-search-panel.component';

export abstract class AbstractIdeaList implements OnInit, OnDestroy {
  /**
   * Page size
   */
  pageSize: number = 12;

  /**
   * Current status to filter
   */
  currentStatus: Status;

  /**
   * Search params
   */
  searchParams: { [key: string]: any } = {};

  /**
   * List of ideas by statusId
   */
  ideas: { [statusId: number]: SuccessResponseObject } = {};

  /**
   * List of categories
   */
  categories: Category[] = [];

  /**
   * Truncate options for the title
   */
  titleTruncateOptions: TruncateOptions = { length: 30 };

  /**
   * Truncate options for the description
   */
  descriptionTruncateOptions: TruncateOptions = { length: 100 };

  /**
   * Last search was ...
   */
  protected _lastSearch: any;

  /**
   * Subscriptions
   */
  protected _subs: Set<Subscription> = new Set();

  /**
   * Constructor
   *
   * @param _activatedRoute
   * @param _loadingService
   * @param _ideaService
   */
  constructor(
    protected _activatedRoute: ActivatedRoute,
    protected _loadingService: TdLoadingService,
    protected _ideaService: IdeaService
  ) {
    this.currentStatus = this.statuses[0];
  }

  /**
   * Init
   */
  ngOnInit() {
    this._subs.add(
      this._activatedRoute.queryParams.subscribe((params) => {
        const { categoryId } = params;

        this.searchParams = Object.assign(this.searchParams, { categoryId });

        this._getAllIdeas();
      })
    );
  }

  /**
   * Destroy
   */
  ngOnDestroy() {
    this._subs.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Sorting object
   */
  protected get _sort() {
    return {
      orders: [
        {
          property: get(this.searchParams, 'sort.property', 'id'),
          direction: get(this.searchParams, 'direction.direction', 'DESC')
        }
      ]
    };
  }

  /**
   * Returns observables
   */
  protected get _observables$() {
    const observables$ = this.statuses.map((tab) => this._getIdeas(tab));

    // Get Categories
    observables$.push(this._getCategories());

    return observables$;
  }

  /**
   * Returns a list of statuses to filter categories
   */
  protected get _statuses() {
    const statuses = [];
    this.statuses.forEach((status) =>
      statuses.push(status.statusId, ...status.includes)
    );
    return statuses;
  }

  /**
   * List of statuses/tabs
   */
  get statuses() {
    return IdeaService.statuses.filter((status) => !status.profile);
  }

  /**
   * Gets all ideas
   */
  protected _getAllIdeas() {
    this._loadingService.register();
    Observable.zip(...this._observables$)
      .finally(() => {
        this._loadingService.resolve();
      })
      .subscribe((results: any[]) => {
        const statusIndex = results
          .slice(0, this.statuses.length)
          .findIndex((res) => res['result'].data && res.result.page.total > 0);
        this.currentStatus = this.statuses[Math.max(statusIndex, 0)];
      });
  }

  /**
   * Gets ideas by status (base)
   *
   * @param status
   * @param data
   * @param paing
   */
  protected _getIdeasBase(
    status: Status,
    data: { [key: string]: any },
    paging: IPaging,
    sort: IPagingSort
  ) {
    return this._ideaService
      .searchFull(data, paging, [...sort.orders])
      .do((res: any) => {
        if (res.result.errors) {
          console.log('error response of ideas search', res);
        } else if (res.result.data) {
          this.ideas[status.statusId] = new SuccessResponseObject(res.result);
        }
      });
  }

  /**
   * Gets ideas by status
   *
   * @param status
   * @param page
   * @param size
   */
  protected _getIdeas(
    status: Status,
    page: number = 0,
    size: number = this.pageSize
  ) {
    const data = {
      ...{ statusIds: [status.statusId, ...status.includes] },
      ...this.searchParams
    };
    return this._getIdeasBase(status, data, { page, size }, { ...this._sort });
  }

  /**
   * Gets categories
   */
  protected _getCategoriesBase(data: { [key: string]: any }) {
    return this._ideaService.categorySearchWithCounts(data).do((res: any) => {
      if (res.result.errors) {
        // console.log(res);
      } else if (res['result'].data) {
        this.categories = res['result'].data;
      }
    });
  }

  /**
   * Gets categories
   */
  protected _getCategories() {
    const data = Object.assign({ statusIds: this._statuses }, {});
    return this._getCategoriesBase(data);
  }

  /**
   * Page change event
   *
   * @param status
   * @param page
   */
  pageChanged(
    status: Status,
    page: { pageIndex: number; pageSize: number; length: number }
  ) {
    this._loadingService.register();
    this._getIdeas(status, page.pageIndex, page.pageSize)
      .finally(() => {
        this._loadingService.resolve();
      })
      .subscribe((res) => {});
  }

  /**
   * Search method
   *
   * @param searchParams
   */
  doSearch(searchParams: SearchParams) {
    clearTimeout(this._lastSearch);
    this._lastSearch = setTimeout(() => {
      this.searchParams = Object.assign(this.searchParams, searchParams);
      this._getAllIdeas();
    }, 1000);
  }
}



// WEBPACK FOOTER //
// /opt/app-root/src/src/app/shared/abstract-idea-list.ts